import type { VideoFilterService, VideoFilterSettings } from '../ports';

type MediaPipeTasksVision = typeof import('@mediapipe/tasks-vision');

const DEFAULT_WIDTH = 1280;
const DEFAULT_HEIGHT = 720;
const DEFAULT_FPS = 30;

export class MediaPipeCanvasVideoFilterService implements VideoFilterService {
  private outputStream: MediaStream | null = null;
  private animationFrameId: number | null = null;
  private sourceVideo: HTMLVideoElement | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private mediaPipePromise: Promise<MediaPipeTasksVision | null> | null = null;
  private settings: VideoFilterSettings = { filterId: 'none', intensity: 0.65 };

  async process(input: MediaStream, settings: VideoFilterSettings): Promise<MediaStream> {
    const videoTrack = input.getVideoTracks()[0];
    if (!videoTrack) {
      this.stop();
      this.settings = settings;
      return input;
    }

    if (this.outputStream && this.sourceVideo && this.sourceVideo.srcObject) {
      const outputTrack = this.outputStream.getVideoTracks()[0];
      if (outputTrack && outputTrack.readyState === 'live') {
        const currentSourceStream = this.sourceVideo.srcObject as MediaStream;
        const currentSourceTrack = currentSourceStream.getVideoTracks()[0];
        if (currentSourceTrack === videoTrack) {
          this.settings = settings;
          return this.outputStream;
        }
      }
    }

    this.stop();
    this.settings = settings;

    void this.loadMediaPipeRuntime();

    const { width, height, frameRate } = this.getTrackShape(videoTrack);
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream([videoTrack]);

    await video.play();

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const output = canvas.captureStream(frameRate);
    input.getAudioTracks().forEach((track) => output.addTrack(track));

    this.sourceVideo = video;
    this.canvas = canvas;
    this.outputStream = output;

    this.renderLoop();

    return output;
  }

  updateSettings(settings: VideoFilterSettings): void {
    this.settings = settings;
  }

  stop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    this.outputStream?.getVideoTracks().forEach((track) => track.stop());
    this.outputStream = null;

    if (this.sourceVideo) {
      this.sourceVideo.pause();
      this.sourceVideo.srcObject = null;
      this.sourceVideo = null;
    }

    this.canvas = null;
  }

  private renderLoop(): void {
    const draw = () => {
      const video = this.sourceVideo;
      const canvas = this.canvas;
      const ctx = canvas?.getContext('2d', { alpha: false });

      if (!video || !canvas || !ctx) {
        return;
      }

      this.drawFilteredFrame(ctx, video, canvas, this.settings);
      this.animationFrameId = requestAnimationFrame(draw);
    };

    draw();
  }

  private drawFilteredFrame(
    ctx: CanvasRenderingContext2D,
    video: HTMLVideoElement,
    canvas: HTMLCanvasElement,
    settings: VideoFilterSettings,
  ): void {
    const intensity = Math.max(0, Math.min(1, settings.intensity));
    ctx.save();
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    if (settings.filterId === 'background-blur') {
      ctx.filter = `blur(${Math.round(6 + intensity * 22)}px) saturate(${1 + intensity * 0.1})`;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      ctx.filter = 'none';
      this.clipPresenterOval(ctx, canvas);
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      ctx.restore();
      return;
    }

    if (settings.filterId === 'soft-mask') {
      ctx.filter = `saturate(${1 + intensity * 0.25}) contrast(${1 + intensity * 0.08})`;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      ctx.filter = 'none';
      this.drawSoftMask(ctx, canvas, intensity);
      ctx.restore();
      return;
    }

    if (settings.filterId === 'color-correct') {
      ctx.filter = `brightness(${1 + intensity * 0.08}) contrast(${1 + intensity * 0.14}) saturate(${
        1 + intensity * 0.22
      })`;
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      ctx.restore();
      return;
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();
  }

  private clipPresenterOval(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement): void {
    ctx.beginPath();
    ctx.ellipse(
      canvas.width * 0.5,
      canvas.height * 0.48,
      canvas.width * 0.27,
      canvas.height * 0.43,
      0,
      0,
      Math.PI * 2,
    );
    ctx.clip();
  }

  private drawSoftMask(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, intensity: number): void {
    const radius = Math.min(canvas.width, canvas.height) * (0.28 + intensity * 0.08);
    const gradient = ctx.createRadialGradient(
      canvas.width * 0.5,
      canvas.height * 0.5,
      radius * 0.55,
      canvas.width * 0.5,
      canvas.height * 0.5,
      radius,
    );

    gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
    gradient.addColorStop(1, `rgba(12, 18, 28, ${0.35 + intensity * 0.35})`);

    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
  }

  private getTrackShape(track: MediaStreamTrack): { width: number; height: number; frameRate: number } {
    const settings = track.getSettings();

    return {
      width: settings.width ?? DEFAULT_WIDTH,
      height: settings.height ?? DEFAULT_HEIGHT,
      frameRate: settings.frameRate ?? DEFAULT_FPS,
    };
  }

  private async loadMediaPipeRuntime(): Promise<MediaPipeTasksVision | null> {
    if (!this.mediaPipePromise) {
      this.mediaPipePromise = import('@mediapipe/tasks-vision')
        .then((runtime) => runtime)
        .catch(() => null);
    }

    return this.mediaPipePromise;
  }
}
